import React from 'react';
import { generateCalendarDates, getMonthName, formatDateForDisplay } from '../../../constants/realVenueData';
import { FILTER_OPTIONS, SORT_OPTIONS } from '../constants';
import { isSearchPriceFilterEnabled } from '../../../utils/venuePricing';
import { FigmaChevronDownIcon } from '../../icons';
import './FilterBar.css';

const FieldChevron = () => (
  <FigmaChevronDownIcon className="filter-field-chevron" aria-hidden="true" />
);

const FilterBar = ({
  filters,
  showFilters,
  showDatePicker,
  showSortDropdown,
  sortBy,
  currentMonth,
  currentYear,
  selectedDate,
  onToggleDatePicker,
  onToggleFilter,
  onToggleSort,
  onFilterSelect,
  onSortSelect,
  onDateSelect,
  onMonthChange
}) => {
  const showPriceFilter = isSearchPriceFilterEnabled();
  const sortOptions = showPriceFilter
    ? SORT_OPTIONS
    : SORT_OPTIONS.filter((o) => o.value !== 'price-low' && o.value !== 'price-high');

  return (
    <div className="filter-bar">
      <div className="filter-content">
        <div className="filter-group">
          {/* Date Filter */}
          <div className="filter-dropdown-container">
            <button 
              className="filter-dropdown"
              onClick={onToggleDatePicker}
            >
              {selectedDate ? formatDateForDisplay(selectedDate) : 'Date'}
              <FieldChevron />
            </button>
            {showDatePicker && (
              <div className="filter-date-picker">
                <div className="date-picker-header">
                  <button 
                    type="button" 
                    className="month-nav-btn"
                    onClick={() => onMonthChange('prev')}
                  >
                    ←
                  </button>
                  <span className="date-picker-month">{getMonthName(currentMonth)} {currentYear}</span>
                  <button 
                    type="button" 
                    className="month-nav-btn"
                    onClick={() => onMonthChange('next')}
                  >
                    →
                  </button>
                </div>
                <div className="date-picker-grid">
                  <div className="date-picker-weekdays">
                    <span>Su</span>
                    <span>Mo</span>
                    <span>Tu</span>
                    <span>We</span>
                    <span>Th</span>
                    <span>Fr</span>
                    <span>Sa</span>
                  </div>
                  <div className="date-picker-days">
                    {generateCalendarDates(currentYear, currentMonth).map((date, index) => (
                      <button
                        key={`${currentYear}-${currentMonth}-${index}`}
                        type="button"
                        className={`date-picker-day ${date ? 'has-date' : 'empty'} ${date?.disabled ? 'disabled' : ''}`}
                        onClick={() => {
                          if (date && !date.disabled) {
                            onDateSelect(date);
                          }
                        }}
                        disabled={!date || date.disabled}
                      >
                        {date ? date.day : ''}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {showPriceFilter && (
          <div className="filter-dropdown-container">
            <button 
              className="filter-dropdown"
              onClick={() => onToggleFilter('price')}
            >
              {filters.price ? FILTER_OPTIONS.price.find(p => p.value === filters.price)?.label : 'Price'}
              <FieldChevron />
            </button>
            {showFilters.price && (
              <div className="filter-dropdown-menu">
                {FILTER_OPTIONS.price.map((option) => (
                  <div
                    key={option.value}
                    className={`filter-dropdown-item ${filters.price === option.value ? 'selected' : ''}`}
                    onClick={() => onFilterSelect('price', option.value)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Vibe Filter */}
          <div className="filter-dropdown-container">
            <button 
              className="filter-dropdown"
              onClick={() => onToggleFilter('vibe')}
            >
              {filters.vibe || 'Vibe'}
              <FieldChevron />
            </button>
            {showFilters.vibe && (
              <div className="filter-dropdown-menu">
                {FILTER_OPTIONS.vibe.map((option) => (
                  <div
                    key={option}
                    className={`filter-dropdown-item ${filters.vibe === option ? 'selected' : ''}`}
                    onClick={() => onFilterSelect('vibe', option)}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Event Type Filter */}
          <div className="filter-dropdown-container">
            <button 
              className="filter-dropdown"
              onClick={() => onToggleFilter('eventType')}
            >
              {filters.eventType || 'Event type'}
              <FieldChevron />
            </button>
            {showFilters.eventType && (
              <div className="filter-dropdown-menu">
                {FILTER_OPTIONS.eventType.map((option) => (
                  <div
                    key={option}
                    className={`filter-dropdown-item ${filters.eventType === option ? 'selected' : ''}`}
                    onClick={() => onFilterSelect('eventType', option)}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attendees Filter */}
          <div className="filter-dropdown-container">
            <button 
              className="filter-dropdown"
              onClick={() => onToggleFilter('attendees')}
            >
              {filters.attendees ? FILTER_OPTIONS.attendees.find(a => a.value === filters.attendees)?.label : 'Attendees'}
              <FieldChevron />
            </button>
            {showFilters.attendees && (
              <div className="filter-dropdown-menu">
                {FILTER_OPTIONS.attendees.map((option) => (
                  <div
                    key={option.value}
                    className={`filter-dropdown-item ${filters.attendees === option.value ? 'selected' : ''}`}
                    onClick={() => onFilterSelect('attendees', option.value)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* More Filters */}
          <div className="filter-dropdown-container">
            <button 
              className="filter-dropdown"
              onClick={() => onToggleFilter('moreFilters')}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 5.41667C10.4602 5.41667 10.8333 5.04357 10.8333 4.58333C10.8333 4.1231 10.4602 3.75 10 3.75C9.53976 3.75 9.16667 4.1231 9.16667 4.58333C9.16667 5.04357 9.53976 5.41667 10 5.41667Z" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 16.25C10.4602 16.25 10.8333 15.8769 10.8333 15.4167C10.8333 14.9564 10.4602 14.5833 10 14.5833C9.53976 14.5833 9.16667 14.9564 9.16667 15.4167C9.16667 15.8769 9.53976 16.25 10 16.25Z" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4.58333 10H5.41667" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14.5833 10H15.4167" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 4.58333V5.41667" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 14.5833V15.4167" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              More Filters
            </button>
            {showFilters.moreFilters && (
              <div className="filter-dropdown-menu">
                <div className="filter-dropdown-item">
                  Additional filter options coming soon
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sort Dropdown */}
        <div className="filter-right">
          <div className="filter-dropdown-container">
            <button 
              className="filter-dropdown"
              onClick={onToggleSort}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5 5H17.5" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 10H15" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.5 15H12.5" stroke="#2A2A2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {sortOptions.find(s => s.value === sortBy)?.label || SORT_OPTIONS.find(s => s.value === sortBy)?.label || 'Popularity'}
              <FieldChevron />
            </button>
            {showSortDropdown && (
              <div className="filter-dropdown-menu">
                {sortOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`filter-dropdown-item ${sortBy === option.value ? 'selected' : ''}`}
                    onClick={() => onSortSelect(option.value)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;

